import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { API_BASE } from '../config';
import { api, errorText } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  Home,
  Briefcase,
  FileText,
  BarChart3,
  Building2,
  UserCircle,
  Zap,
  Kanban,
  Users,
  Brain,
  MessageSquare,
  Activity,
  Linkedin,
  GitBranch,
  Target,
  Compass,
  FolderOpen,
  UserCog,
  Gauge,
} from 'lucide-react';

interface OverviewStats {
  jobs: number;
  companies: number;
  resumes: number;
  analyses: number;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.FC<{ className?: string }>;
  description: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState<OverviewStats>({ jobs: 0, companies: 0, resumes: 0, analyses: 0 });

  useEffect(() => {
    fetch(`${API_BASE}/api/stats/overview`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error('unauthorized'); return r.json(); })
      .then((data) => setStats({
        jobs: data.jobs ?? 0,
        companies: data.companies ?? 0,
        resumes: data.resumes ?? 0,
        analyses: data.analyses ?? 0,
      }))
      .catch(() => {});
  }, []);

  const navigationGroups: NavGroup[] = [
    {
      label: 'Home',
      items: [
        { name: 'Start Here', href: '/start-here', icon: Compass, description: 'Your guide to ManSearch' },
        { name: 'Dashboard', href: '/', icon: Home, description: 'Overview & quick stats' },
      ],
    },
    {
      label: 'Your Profile',
      items: [
        { name: 'Profile', href: '/settings', icon: UserCircle, description: 'Skills, goals & preferences' },
        { name: 'Master Profile', href: '/master-profile', icon: UserCog, description: 'Verified source of truth for generation' },
        { name: 'Resumes', href: '/resumes', icon: FileText, description: 'Upload & manage resumes' },
        { name: 'My Documents', href: '/documents', icon: FolderOpen, description: 'All docs & profile enrichment' },
        { name: 'LinkedIn Optimizer', href: '/linkedin-optimizer', icon: Linkedin, description: 'Optimize for recruiter search' },
      ],
    },
    {
      label: 'Discover',
      items: [
        { name: 'Jobs', href: '/jobs', icon: Briefcase, description: 'Browse & filter job listings' },
        { name: 'Companies', href: '/companies', icon: Building2, description: 'Company research & tracking' },
      ],
    },
    {
      label: 'AI Insights',
      items: [
        { name: 'Market Analytics', href: '/analytics', icon: Activity, description: 'Trends, salary & demand data' },
        { name: 'Intelligence', href: '/intelligence', icon: Brain, description: 'Skill ranking & opportunities' },
        { name: 'Skill Gap Analysis', href: '/skill-gap-analysis', icon: GitBranch, description: 'Identify missing skills' },
        { name: 'Match Analysis', href: '/analysis', icon: BarChart3, description: 'Resume-to-job scoring' },
      ],
    },
    {
      label: 'Take Action',
      items: [
        { name: 'Job Targeting', href: '/job-targeting', icon: Target, description: 'Generate ATS resume & cover letter' },
        { name: 'Interview Prep', href: '/interview', icon: MessageSquare, description: 'Practice with AI coach' },
        { name: 'Application Tracker', href: '/tracker', icon: Kanban, description: 'Track your pipeline' },
      ],
    },
  ];

  if (isAdmin) {
    navigationGroups.push({
      label: 'Admin',
      items: [
        { name: 'Manage Users', href: '/admin/users', icon: Users, description: 'User administration' },
        { name: 'Telemetry', href: '/admin/telemetry', icon: Gauge, description: 'Usage, AI cost & product analytics' },
      ],
    });
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <motion.div
      className="w-64 bg-white dark:bg-navy-900 border-r border-slate-200 dark:border-slate-800 flex-shrink-0"
      initial={{ x: -100 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="p-4 flex flex-col h-full overflow-hidden">
        {/* Admin Scrape Button */}
        {isAdmin && (
          <div className="mb-4">
            <motion.button
              className="w-full flex items-center space-x-3 px-3 py-2.5 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-500/20 transition-colors duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                api.post('/api/scrape/all', {})
                  .then(() => toast.success('Scraping started!'))
                  .catch((e) => toast.error(errorText(e, 'Failed to start scraping')));
              }}
            >
              <Zap className="w-4 h-4" />
              <span>Start Scraping</span>
            </motion.button>
          </div>
        )}

        {/* Grouped Navigation */}
        <nav className="flex-1 space-y-5 overflow-y-auto custom-scrollbar">
          {navigationGroups.map((group) => (
            <div key={group.label}>
              <h3 className="text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest mb-1.5 px-3">
                {group.label}
              </h3>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.name}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Link
                        to={item.href}
                        className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'bg-primary-500/10 text-primary-600 dark:text-primary-400'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        <Icon
                          className={`mr-3 h-5 w-5 flex-shrink-0 transition-colors duration-200 ${
                            isActive(item.href)
                              ? 'text-primary-500'
                              : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300'
                          }`}
                        />
                        <span className="truncate">{item.name}</span>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Stats Summary */}
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
          <h3 className="text-[10px] font-bold text-slate-400 dark:text-slate-400 uppercase tracking-widest mb-2 px-3">
            Your Stats
          </h3>
          <div className="space-y-1.5 px-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400">Jobs Scraped</span>
              <span className="font-medium text-slate-800 dark:text-white">{stats.jobs.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400">Companies</span>
              <span className="font-medium text-slate-800 dark:text-white">{stats.companies.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400">Resumes</span>
              <span className="font-medium text-slate-800 dark:text-white">{stats.resumes.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400">Analyses</span>
              <span className="font-medium text-slate-800 dark:text-white">{stats.analyses.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Sidebar;
